import { FormEvent, useMemo, useState } from 'react'
import isEmail from 'validator/lib/isEmail'

import type { RegisterRequestBody } from 'services/sso/types'
import Loader from 'content-components/_Primatives/Loader'
import Input from '../../Input'
import Label from '../../Label'
import Button from '../../Button'
import Checkbox from '../../Checkbox'
import Select from '../../Select'
import useFormFields from '../../useFormFields'
import useBirthdateOptions from './useBirthdateOptions'
import style from './style.scss'

type FormFields = {
  firstName: string
  lastName: string
  birthMonth?: string
  birthDay?: string
  birthYear?: string
  email: string
  emailConfirm: string
  password: string
  passwordConfirm: string
  terms: boolean
  privacyPolicy: boolean
}

type Props = {
  onSubmit: (params: RegisterRequestBody) => Promise<void>
  fullNameText: string
  firstNameText: string
  lastNameText: string
  invalidNameText: string
  yourBirthdayText: string
  monthText: string
  yearText: string
  dayText: string
  invalidBirthText: string
  ageDisclaimerAccountText: string
  emailText: string
  invalidEmailText: string
  emailConfirmText: string
  invalidEmailConfirmText: string
  passwordText: string
  passwordConfirmText: string
  invalidPasswordText: string
  invalidPasswordConfirmText: string
  passwordInstructionText: string
  termsText: string
  privacyText: string
  invalidPrivacyTermsText: string
  buttonText: string
}

function CreateAccountForm({
  onSubmit,
  fullNameText,
  firstNameText,
  lastNameText,
  invalidNameText,
  yourBirthdayText,
  monthText,
  yearText,
  dayText,
  invalidBirthText,
  ageDisclaimerAccountText,
  emailText,
  invalidEmailText,
  emailConfirmText,
  invalidEmailConfirmText,
  passwordText,
  passwordConfirmText,
  invalidPasswordText,
  invalidPasswordConfirmText,
  passwordInstructionText,
  termsText,
  privacyText,
  invalidPrivacyTermsText,
  buttonText,
}: Props) {
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const {
    firstName,
    lastName,
    birthMonth,
    birthDay,
    birthYear,
    email,
    emailConfirm,
    password,
    passwordConfirm,
    terms,
    privacyPolicy,
    onChange,
  } = useFormFields<FormFields>({
    firstName: '',
    lastName: '',
    birthMonth: undefined,
    birthDay: undefined,
    birthYear: undefined,
    email: '',
    emailConfirm: '',
    password: '',
    passwordConfirm: '',
    terms: false,
    privacyPolicy: false,
  })

  const { yearOptions, dayOptions, monthOptions } = useBirthdateOptions({
    month: birthMonth,
    year: birthYear,
  })

  const isNameValid = useMemo(() => {
    return firstName && lastName
  }, [firstName, lastName])

  const isBirthValid = useMemo(() => {
    return birthMonth && birthDay && birthYear
  }, [birthMonth, birthDay, birthYear])

  const isEmailValid = useMemo(() => {
    return isEmail(email)
  }, [email])

  const doesEmailMatchConfirmation = useMemo(() => {
    return email === emailConfirm
  }, [email, emailConfirm])

  const isPasswordValid = useMemo(() => {
    return !!password
  }, [password])

  const doesPasswordMatchConfirmation = useMemo(() => {
    return password === passwordConfirm
  }, [password, passwordConfirm])

  const isTermsAccepted = useMemo(() => {
    return !!terms
  }, [terms])

  const isPrivacyPolicyAccepted = useMemo(() => {
    return !!privacyPolicy
  }, [privacyPolicy])

  const hasErrors =
    !isNameValid ||
    !isBirthValid ||
    !isEmailValid ||
    !doesEmailMatchConfirmation ||
    !isPasswordValid ||
    !doesPasswordMatchConfirmation ||
    !isTermsAccepted ||
    !isPrivacyPolicyAccepted

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setHasSubmitted(true)

    if (hasErrors) {
      return
    }

    if (birthMonth && birthDay && birthYear) {
      setIsLoading(true)

      onSubmit({
        firstName,
        lastName,
        birthMonth,
        birthDay,
        birthYear,
        email,
        password,
      }).catch((error: Error) => {
        setError(error.message)
        setIsLoading(false)
      })
    }
  }

  function handleChangeInput(event: FormEvent<HTMLInputElement>) {
    const { name, value } = event.currentTarget
    if (
      name === 'firstName' ||
      name === 'lastName' ||
      name === 'email' ||
      name === 'emailConfirm' ||
      name === 'password' ||
      name === 'passwordConfirm' ||
      name === 'terms' ||
      name === 'privacyPolicy'
    ) {
      onChange({
        name,
        value,
      })
    }
  }

  function handleChangeSelect(event: FormEvent<HTMLSelectElement>) {
    const { name, value } = event.currentTarget
    if (name === 'birthMonth' || name === 'birthDay' || name === 'birthYear') {
      onChange({
        name,
        value,
      })
    }
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <form className={style.wrapper} onSubmit={handleSubmit}>
      <Label className={style.label} htmlFor='first-name'>
        {fullNameText}
      </Label>
      <div className={style.fieldGroup}>
        <Input
          className={style.input}
          id='first-name'
          name='firstName'
          placeholder={firstNameText}
          type='text'
          value={firstName}
          onChange={handleChangeInput}
        />
        <Input
          className={style.input}
          name='lastName'
          placeholder={lastNameText}
          type='text'
          value={lastName}
          onChange={handleChangeInput}
        />
      </div>
      {hasSubmitted && !isNameValid && (
        <p className={style.error}>{invalidNameText}</p>
      )}

      <Label className={style.label} htmlFor='birth-month'>
        {yourBirthdayText}
      </Label>
      <div className={style.fieldGroup}>
        <Select
          className={style.select}
          id='birth-month'
          name='birthMonth'
          value={birthMonth}
          onChange={handleChangeSelect}
        >
          <option>{monthText}</option>
          {monthOptions.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          className={style.select}
          id='birth-day'
          name='birthDay'
          value={birthDay}
          onChange={handleChangeSelect}
        >
          <option>{dayText}</option>
          {dayOptions.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          className={style.select}
          id='birth-year'
          name='birthYear'
          value={birthYear}
          onChange={handleChangeSelect}
        >
          <option>{yearText}</option>
          {yearOptions.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      {hasSubmitted && !isBirthValid && (
        <p className={style.error}>{invalidBirthText}</p>
      )}

      <p className={style.note}>{ageDisclaimerAccountText}</p>

      <div className={style.section}>
        <Label className={style.label} htmlFor='email'>
          {emailText}
        </Label>
        <Input
          className={style.input}
          id='email'
          name='email'
          placeholder={emailText}
          type='email'
          value={email}
          onChange={handleChangeInput}
        />
        {hasSubmitted && !isEmailValid && (
          <p className={style.error}>{invalidEmailText}</p>
        )}
        <Input
          className={style.input}
          name='emailConfirm'
          placeholder={emailConfirmText}
          type='email'
          value={emailConfirm}
          onChange={handleChangeInput}
        />
      </div>

      {hasSubmitted && !doesEmailMatchConfirmation && (
        <p className={style.error}>{invalidEmailConfirmText}</p>
      )}

      <Label className={style.label} htmlFor='password'>
        {passwordText}
      </Label>
      <Input
        className={style.input}
        id='password'
        name='password'
        placeholder={passwordText}
        type='password'
        value={password}
        onChange={handleChangeInput}
      />

      {hasSubmitted && !isPasswordValid && (
        <p className={style.error}>{invalidPasswordText}</p>
      )}

      <Input
        className={style.input}
        name='passwordConfirm'
        placeholder={passwordConfirmText}
        type='password'
        value={passwordConfirm}
        onChange={handleChangeInput}
      />

      {hasSubmitted && !doesPasswordMatchConfirmation && (
        <p className={style.error}>{invalidPasswordConfirmText}</p>
      )}

      <p className={style.note}>{passwordInstructionText}</p>

      <div className={style.checkboxGroup}>
        <Checkbox
          checked={terms}
          className={style.checkboxInput}
          id='terms'
          name='terms'
          onChange={handleChangeInput}
        />
        <Label
          className={style.checkboxLabel}
          dangerouslySetInnerHTML={{ __html: termsText }}
          htmlFor='terms'
        />
      </div>

      <div className={style.checkboxGroup}>
        <Checkbox
          checked={privacyPolicy}
          className={style.checkboxInput}
          id='privacy-policy'
          name='privacyPolicy'
          onChange={handleChangeInput}
        />
        <Label
          className={style.checkboxLabel}
          dangerouslySetInnerHTML={{ __html: privacyText }}
          htmlFor='privacy-policy'
        />
      </div>

      {hasSubmitted && (!isTermsAccepted || !isPrivacyPolicyAccepted) && (
        <p className={style.error}>{invalidPrivacyTermsText}</p>
      )}

      {error && <p className={style.error}>{error}</p>}

      <Button type='submit' className={style.button}>
        {buttonText}
      </Button>
    </form>
  )
}

export default CreateAccountForm
